import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Search, MessageCircle, User, Shield, HeadphonesIcon, BookOpen, BarChart3, UserCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { haptics } from "@/lib/haptics";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { onNotificationsRead } from "@/lib/unreadCountBus";

interface NavigationProps {
  viewMode: 'platform' | 'background';
  isAdmin: boolean;
  isModerator: boolean;
  isSupport: boolean;
}

const Navigation = ({ viewMode, isAdmin, isModerator, isSupport }: NavigationProps) => {
  const location = useLocation();
  const { user } = useAuth();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  useEffect(() => {
    if (!user) return;

    const fetchUnreadCount = async () => {
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('type', 'message')
        .eq('is_read', false);
      setUnreadMessageCount(count || 0);
    };

    fetchUnreadCount();

    const channel = supabase
      .channel('nav-message-badge')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        fetchUnreadCount();
      })
      .subscribe();

    // Instant sync when the Notifications page marks notifications read.
    // This badge only counts type='message', so refetch its own count.
    const offBus = onNotificationsRead(() => {
      fetchUnreadCount();
    });

    return () => {
      offBus();
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Clear badge when on messages page
  useEffect(() => {
    if (location.pathname.startsWith('/app/messages') && unreadMessageCount > 0 && user) {
      supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('type', 'message')
        .eq('is_read', false)
        .then(() => setUnreadMessageCount(0));
    }
  }, [location.pathname, unreadMessageCount, user]);

  const platformItems = [{
    icon: Home,
    label: "Home",
    path: "/app"
  }, {
    icon: Search,
    label: "Rooms",
    path: "/app/discovery"
  }, {
    icon: UserCheck,
    label: "Providers",
    path: "/app/therapists"
  }, {
    icon: MessageCircle,
    label: "Messages",
    path: "/app/messages"
  }, {
    icon: User,
    label: "Profile",
    path: "/app/profile"
  }];

  const backgroundItems = [
    ...(isModerator ? [{
      icon: Shield,
      label: "Moderation",
      path: "/app/moderation"
    }] : []),
    ...((isSupport || isAdmin) ? [{
      icon: HeadphonesIcon,
      label: "Support",
      path: "/app/support-dashboard"
    }] : []),
    ...((isModerator || isAdmin) ? [{
      icon: UserCheck,
      label: "Verify",
      path: "/app/therapist-verification"
    }] : []),
    ...(isAdmin ? [
      {
        icon: BookOpen,
        label: "Knowledge",
        path: "/app/knowledge-base"
      },
      {
        icon: BarChart3,
        label: "Analytics",
        path: "/app/analytics"
      }
    ] : [])
  ];

  const navItems = viewMode === 'platform' ? platformItems : backgroundItems;

  const handleNavClick = () => {
    haptics.light();
  };

  return <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border z-50 pb-[env(safe-area-inset-bottom,0px)]">
      <nav className="flex items-center justify-around py-3 px-4 max-w-md mx-auto">
        {navItems.map(item => {
        const isActive = location.pathname === item.path;
        const Icon = item.icon;
        const showBadge = item.path === '/app/messages' && unreadMessageCount > 0;
        return <Link 
            key={item.path} 
            to={item.path} 
            data-community-tour={item.label.toLowerCase()}
            onClick={handleNavClick}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            className={cn("flex flex-col items-center gap-1.5 p-2.5 rounded-lg transition-smooth min-w-[64px] relative", isActive ? "text-primary bg-primary-soft" : "text-muted-foreground hover:text-foreground hover:bg-accent")}>
              <div className="relative">
                <Icon className="h-6 w-6" />
                {showBadge && (
                  <span className="absolute -top-1.5 -right-2 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
              </div>
              <span className="text-xs font-medium">{item.label}</span>
            </Link>;
      })}
      </nav>
    </div>;
};
export default Navigation;